import React, { useState, useCallback } from "react";
import { Modal, Tag, TextField, Button } from "@ann/web-components";
import { TagsContainer, AddTagContainer, EmptyState } from "./TagsModal.styled";

const TagsModal = ({
  isOpen,
  onClose,
  onSave,
  initialTags = [],
  size = "medium",
  modalWidth = "600px",
  modalHeight = "auto",
}) => {
  const [tags, setTags] = useState(initialTags);
  const [newTagText, setNewTagText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Handle adding a new tag
  const handleAddTag = useCallback(() => {
    const trimmedTag = newTagText.trim();
    
    if (!trimmedTag) return;
    
    // Check if tag already exists
    if (tags.some(tag => tag.toLowerCase() === trimmedTag.toLowerCase())) {
      console.warn("Tag already exists");
      return;
    }

    setTags(prev => [...prev, trimmedTag]);
    setNewTagText("");
  }, [newTagText, tags]);

  // Handle removing a tag
  const handleRemoveTag = useCallback((tagToRemove) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  }, []);

  // Handle Enter key in input
  const handleKeyPress = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  }, [handleAddTag]);

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      
      if (onSave) {
        await onSave(tags);
      }
      
      onClose();
    } catch (error) {
      console.error("Error saving tags:", error);
    } finally {
      setIsSaving(false);
    }
  }, [tags, onSave, onClose]);

  // Handle close and reset
  const handleClose = useCallback(() => {
    setTags(initialTags);
    setNewTagText("");
    setIsSaving(false);
    onClose();
  }, [initialTags, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width={modalWidth}
      height={modalHeight}
      size={size}
    >
      <Modal.Header>
        <Modal.Header.Title>Edit Tags</Modal.Header.Title>
      </Modal.Header>

      <Modal.Body>
        <TagsContainer>
          {tags.length === 0 ? (
            <EmptyState>No tags added yet. Add your first tag below.</EmptyState>
          ) : (
            tags.map((tag, index) => (
              <Tag
                key={`${tag}-${index}`}
                closable
                color="secondary"
                id={`tag-${index}`}
                onClose={() => handleRemoveTag(tag)}
                size="medium"
                text={tag}
              />
            ))
          )}
        </TagsContainer>

        <AddTagContainer>
          <TextField
            label=""
            placeholder="Enter tag name"
            size="small"
            value={newTagText}
            onChange={(value) => setNewTagText(value)}
            onKeyPress={handleKeyPress}
          />
          <Button
            label="Add Tag"
            type="primary"
            size="medium"
            onClick={handleAddTag}
            disabled={!newTagText.trim()}
          />
        </AddTagContainer>
      </Modal.Body>

      <Modal.Actions align="end">
        <Modal.Button
          label={isSaving ? "Saving..." : "Save Changes"}
          onClick={handleSave}
          disabled={isSaving}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default TagsModal;

